import { useState } from "react";
import { IconLock } from "@tabler/icons-react";
import { EmptyState } from "@/components/ui";
import { useUserPermisos } from "@/lib/auth-store";
import { PERMISOS } from "@/lib/permisos";
import { useCatalogo } from "../hooks/use-reporteria";
import { emptyDef } from "../state/builder-ops";
import type { RepDefinicion as RepDef } from "../services/reporteria.api";
import { ReporteriaInicio } from "./ReporteriaInicio";
import { ReporteriaBuilder } from "./ReporteriaBuilder";
import { SimpleReportView } from "./SimpleReportView";
import { MisReportesPanel } from "./MisReportesPanel";
import { ReporteriaInicioSkeleton } from "./ReporteriaInicioSkeleton";

/**
 * Orquestador de `/reporteria` (Fase 1 · reportes curados primero). Hotspot 92 ·
 * se entra con el permiso `reporteria.usar` (la ruta lo exige con PermissionGate y
 * el backend con la política `reporteria.acceso`); la configuración asignada
 * define QUÉ tablas/columnas se consultan (sin ninguna → catálogo vacío → aviso).
 * Alterna entre tres vistas:
 *  - "inicio": galería de reportes listos (predefinidos + plantillas).
 *  - "resultados": vista simple de un reporte elegido (tabla + filtros del
 *    propio reporte + exportar), sin exponer columnas/joins.
 *  - "builder": el builder libre (modo avanzado), abierto al "Personalizar" un
 *    reporte o al "Crear reporte personalizado".
 */
export function ReporteriaBuilderPage() {
  const catalogo = useCatalogo();
  // Hotspot 92 · CREAR reportes personalizados (y "Personalizar" uno existente, guardar
  // plantillas y columnas personalizadas) exige `reporteria.personalizar` · el backend lo
  // exige también. Sin él (p. ej. el Visor) solo se abren/ven y EXPORTAN los reportes.
  const puedeCrear = useUserPermisos().includes(PERMISOS.reporteriaPersonalizar);
  const [vista, setVista] = useState<"inicio" | "resultados" | "builder">("inicio");
  const [def, setDef] = useState<RepDef>(() => emptyDef());
  const [nombre, setNombre] = useState("Reporte");
  const [generados, setGenerados] = useState(false);

  // Mientras llega el catálogo · la galería de inicio (la vista que sigue) en skeleton.
  if (catalogo.isLoading) return <ReporteriaInicioSkeleton puedeCrear={puedeCrear} />;

  if (catalogo.isError || !catalogo.data) {
    return (
      <div className="p-6">
        <EmptyState
          icon={IconLock}
          title="Sin acceso a reportería"
          description="No se pudo cargar el catálogo de reportería. Intenta de nuevo más tarde."
        />
      </div>
    );
  }

  if (catalogo.data.tablas.length === 0) {
    return (
      <div className="p-6">
        <EmptyState
          icon={IconLock}
          title="Sin configuración de reportería"
          description="No tienes ninguna configuración de reportes asignada. Pídele a un administrador que te habilite tablas y columnas."
        />
      </div>
    );
  }

  const abrirResultados = (d: RepDef, nom: string) => {
    setDef(d);
    setNombre(nom);
    setVista("resultados");
  };
  const personalizar = (d: RepDef) => {
    setDef(d);
    setVista("builder");
  };
  const crear = () => {
    setDef(emptyDef());
    setNombre("Reporte");
    setVista("builder");
  };

  return (
    <>
      {vista === "inicio" && (
        <ReporteriaInicio
          onAbrir={abrirResultados}
          onCrear={crear}
          onVerGenerados={() => setGenerados(true)}
          puedeCrear={puedeCrear}
        />
      )}
      {vista === "resultados" && (
        <SimpleReportView
          initialDef={def}
          nombre={nombre}
          onPersonalizar={personalizar}
          onVolver={() => setVista("inicio")}
          puedeCrear={puedeCrear}
        />
      )}
      {vista === "builder" && (
        <ReporteriaBuilder initialDef={def} onVolver={() => setVista("inicio")} />
      )}
      <MisReportesPanel open={generados} onClose={() => setGenerados(false)} />
    </>
  );
}
